/**
 * Settings Tour Configuration
 * Introduces users to account settings and preferences - navigates through all tabs and sub-tabs
 */

import { TourDefinition } from './tour.types';

export const settingsTour: TourDefinition = {
  id: 'settings-tour',
  name: 'Account Settings',
  page: '/settings',
  version: 4, // Updated with agency export toggle info
  steps: [
    {
      id: 'settings-intro',
      targetSelector: '[data-tour="settings-header"]',
      title: 'Your Settings',
      description:
        'Customize your experience here. Manage your profile, phone numbers, SMS templates, and subscription.',
      placement: 'bottom',
    },
    {
      id: 'settings-tabs',
      targetSelector: '[data-tour="settings-tabs"]',
      title: 'Settings Navigation',
      description:
        'Use these tabs to navigate between different settings sections. Let\'s walk through each one.',
      placement: 'bottom',
    },
    // === ACCOUNT TAB AND SUB-TABS ===
    {
      id: 'account-tab',
      targetSelector: '[data-tour="settings-tab-account"]',
      title: 'Account Settings',
      description:
        'Update your personal information, profile picture, email integration, and security settings like two-factor authentication.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-account"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'account-subtabs',
      targetSelector: '[data-tour="account-subtabs"]',
      title: 'Account Sub-Sections',
      description:
        'Your account settings are organized into four sections: Profile, Security, Connections, and Preferences.',
      placement: 'bottom',
    },
    {
      id: 'account-profile-tab',
      targetSelector: '[data-tour="account-tab-profile"]',
      title: 'Profile Settings',
      description:
        'Update your name, profile picture, contact information, and other personal details here.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="account-tab-profile"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'account-security-tab',
      targetSelector: '[data-tour="account-tab-security"]',
      title: 'Security Settings',
      description:
        'Protect your account with two-factor authentication (MFA), view security activity, change your password, and update your email.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="account-tab-security"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'account-connections-tab',
      targetSelector: '[data-tour="account-tab-connections"]',
      title: 'Connections',
      description:
        'Link your Google or Microsoft account for email integration and calendar sync.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="account-tab-connections"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'account-preferences-tab',
      targetSelector: '[data-tour="account-tab-preferences"]',
      title: 'Preferences',
      description:
        'Configure notifications, privacy settings, leaderboard visibility, and dialer preferences. Agency owners can also control agent export permissions here.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="account-tab-preferences"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    // === AREA CODES TAB AND SUB-TABS ===
    {
      id: 'area-codes-tab',
      targetSelector: '[data-tour="settings-tab-area-codes"]',
      title: 'Area Codes & Phone Numbers',
      description:
        'Manage your phone numbers for calling and texting. Add new area codes, configure AI matching, and view deactivated numbers.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-area-codes"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'area-codes-tabs',
      targetSelector: '[data-tour="area-codes-tabs"]',
      title: 'Area Code Sections',
      description:
        'Switch between managing your existing numbers and purchasing new area codes.',
      placement: 'bottom',
    },
    {
      id: 'area-codes-manage-tab',
      targetSelector: '[data-tour="area-codes-tab-manage"]',
      title: 'Manage Numbers',
      description:
        'View and manage all your phone numbers, see billing info, and handle deactivated numbers.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="area-codes-tab-manage"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'area-codes-manage-numbers',
      targetSelector: '[data-tour="area-codes-manage-numbers"]',
      title: 'Your Phone Numbers',
      description:
        'Here are all your active phone numbers. Click the settings icon on any number to view detailed stats, billing info, and configuration options.',
      placement: 'top',
    },
    {
      id: 'area-codes-deactivated',
      targetSelector: '[data-tour="area-codes-deactivated"]',
      title: 'Deactivated Numbers',
      description:
        'Phone numbers that have expired appear here. You can recover them by paying to reactivate them and keep the same number.',
      placement: 'top',
    },
    {
      id: 'area-codes-purchase-tab',
      targetSelector: '[data-tour="area-codes-tab-purchase"]',
      title: 'Buy Area Codes',
      description:
        'Purchase new phone numbers in specific area codes to improve local caller ID matching with your leads.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="area-codes-tab-purchase"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'area-codes-ai-matching',
      targetSelector: '[data-tour="area-codes-ai-matching"]',
      title: 'AI Area Code Matching',
      description:
        'Configure how the AI selects which phone number to use when calling leads. Choose between Exact matching, Metropolitan area matching, or Nearest area code matching for the best local appearance.',
      placement: 'bottom',
    },
    {
      id: 'area-codes-purchase-grid',
      targetSelector: '[data-tour="area-codes-purchase-grid"]',
      title: 'Purchase Area Codes',
      description:
        'Browse and select area codes by state. Click on area codes to select them, then checkout to purchase phone numbers in those areas.',
      placement: 'top',
    },
    // === SMS SETTINGS TAB ===
    {
      id: 'sms-tab',
      targetSelector: '[data-tour="settings-tab-sms"]',
      title: 'SMS Settings',
      description:
        'Configure your text messaging. Create templates for quick responses and set up sentimental messages for birthdays and holidays.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-sms"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'sms-templates',
      targetSelector: '[data-tour="settings-sms-templates"]',
      title: 'SMS Templates',
      description:
        'Create reusable message templates for common responses. Save time by using templates instead of typing the same messages repeatedly.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-sms-templates"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'sms-sentimentals',
      targetSelector: '[data-tour="settings-sms-sentimentals"]',
      title: 'Sentimental Messages',
      description:
        'Set up automated birthday wishes, holiday greetings, and policy anniversary messages to stay connected with your clients.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-sms-sentimentals"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    // === A2P TAB ===
    {
      id: 'a2p-tab',
      targetSelector: '[data-tour="settings-tab-a2p"]',
      title: 'A2P / 10DLC Verification',
      description:
        'Register your business for A2P 10DLC compliance. This is required for sending SMS messages to US numbers and improves delivery rates.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-a2p"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    // === CARRIERS TAB ===
    {
      id: 'carriers-tab',
      targetSelector: '[data-tour="settings-tab-carriers"]',
      title: 'Carrier Settings',
      description:
        'Manage your insurance carrier connections and credentials. Add or update carrier logins for quoting and application submissions.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-carriers"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    // === PURCHASES TAB ===
    {
      id: 'purchases-tab',
      targetSelector: '[data-tour="settings-tab-purchases"]',
      title: 'Purchase History',
      description:
        'View your transaction history including phone number purchases, lead purchases, and other add-ons.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-purchases"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    // === SUBSCRIPTION TAB ===
    {
      id: 'subscription-tab',
      targetSelector: '[data-tour="settings-tab-subscription"]',
      title: 'Subscription & Billing',
      description:
        'Manage your subscription plan, view usage, and update payment methods. Upgrade to unlock more features!',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="settings-tab-subscription"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
  ],
};
